<script setup>
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert, useTracking } from 'dashboard/composables';
import { trackQuintoEvent } from 'dashboard/helper/lais/trackingEvents';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SupportTicketPortalAPI from 'dashboard/api/supportTicketPortal';
import AgentSupportDialog from '../components/AgentSupportDialog.vue';
import SupportTicketSkeleton from '../components/SupportTicketSkeleton.vue';
import SupportTicketCreateForm from '../components/SupportTicketCreateForm.vue';
import SupportTicketFiltersPanel from '../components/SupportTicketFiltersPanel.vue';
import SupportTicketFilterDrawer from '../components/SupportTicketFilterDrawer.vue';
import SupportTicketListItem from '../components/SupportTicketListItem.vue';
import SupportTicketDetailSummary from '../components/SupportTicketDetailSummary.vue';
import SupportTicketTriagePanel from '../components/SupportTicketTriagePanel.vue';
import SupportTicketOperationPanel from '../components/SupportTicketOperationPanel.vue';
import SupportTicketContextPanel from '../components/SupportTicketContextPanel.vue';
import SupportAssistanceSummaryCards from '../components/SupportAssistanceSummaryCards.vue';
import SupportAssistanceReport from '../components/SupportAssistanceReport.vue';
import SupportAssistancePrepareForm from '../components/SupportAssistancePrepareForm.vue';
import SupportAssistanceWorkOrderPanel from '../components/SupportAssistanceWorkOrderPanel.vue';
import SupportAssistanceOutcomeForm from '../components/SupportAssistanceOutcomeForm.vue';
import SupportTicketEventList from '../components/SupportTicketEventList.vue';
import SupportTicketReplyForm from '../components/SupportTicketReplyForm.vue';
import {
  normalizePortalMeta,
  publicSupportHref,
  slaStatusMeta,
  ticketStatusMeta,
  waitingOnMeta,
} from '../utils/supportTicketPortal';
import {
  buildAssistanceCategoryOptions,
  buildAssistanceContextOptions,
  buildAssistanceOutcomeOptions,
  buildAssistanceResponsibilityOptions,
  buildAssistanceSeverityOptions,
  buildCreatedByOptions,
  buildEvidenceKindOptions,
  buildPriorityOptions,
  buildSlaFilterOptions,
  buildStatusOptions,
  buildTicketTypeOptions,
  buildWaitingOptions,
} from '../utils/supportTicketOptions';
import {
  buildAssistancePayload,
  buildCostPayload,
  buildEvidencePayload,
  buildOutcomePayload,
  buildReopenPayload,
  defaultAssistanceCostForm,
  defaultAssistanceEvidenceForm,
  defaultAssistanceForm,
  defaultAssistanceOutcomeForm,
  defaultAssistanceReopenForm,
  defaultFilterPayload,
  defaultFilters,
  defaultReplyForm,
  defaultTeamHandoverForm,
  defaultTicketForm,
} from '../utils/supportTicketForms';
import { useSupportTicketFormatters } from '../composables/useSupportTicketFormatters';
import { useSupportTicketAttachments } from '../composables/useSupportTicketAttachments';
import { useSupportTicketViewport } from '../composables/useSupportTicketViewport';

const { t, locale } = useI18n();
const account = useAccount();
const agentSupportLabel = 'Suporte TP/CX';
const { track } = useTracking();
const { assistanceLabel, errorMessage } = useSupportTicketFormatters();
const { uploadErrorMessage } = useSupportTicketAttachments();
const { isMobile, mobileDetailOpen } = useSupportTicketViewport();

const tickets = ref([]);
const selectedTicket = ref(null);
const selectedTicketId = ref(null);
const assistanceReport = ref(null);
const filterPayload = ref(defaultFilterPayload());
const portalMeta = ref(normalizePortalMeta({}));
const isLoading = ref(false);
const isLoadingDetail = ref(false);
const isLoadingAssistanceReport = ref(false);
const isCreating = ref(false);
const isReplying = ref(false);
const isUpdatingAssignee = ref(false);
const isUpdatingPriority = ref(false);
const isUpdatingTeamHandover = ref(false);
const isPreparingAssistance = ref(false);
const isReopeningAssistance = ref(false);
const isApprovingAssistanceBudget = ref(false);
const isRecordingAssistanceCost = ref(false);
const isRecordingAssistanceEvidence = ref(false);
const isRecordingAssistanceOutcome = ref(false);
const filters = ref(defaultFilters());
const ticketForm = ref(defaultTicketForm());
const replyForm = ref(defaultReplyForm());
const assistanceForm = ref(defaultAssistanceForm());
const assistanceReopenForm = ref(defaultAssistanceReopenForm());
const assistanceCostForm = ref(defaultAssistanceCostForm());
const assistanceEvidenceForm = ref(defaultAssistanceEvidenceForm());
const assistanceOutcomeForm = ref(defaultAssistanceOutcomeForm());
const teamHandoverForm = ref(defaultTeamHandoverForm());
const assistanceActionError = ref('');
const showFilterDrawer = ref(false);
const showAgentSupportDialog = ref(false);

const canViewAccountTickets = computed(
  () => portalMeta.value.canViewAccountTickets
);
const hasTickets = computed(() => tickets.value.length > 0);
const selectedEvents = computed(() => selectedTicket.value?.events || []);
const selectedSla = computed(() => selectedTicket.value?.sla || null);
const selectedRealEstate = computed(
  () => selectedTicket.value?.real_estate || {}
);
const selectedAssistance = computed(
  () => selectedTicket.value?.assistance || {}
);
const selectedAssistanceTicket = computed(
  () => selectedAssistance.value.ticket || {}
);
const selectedMaintenanceCase = computed(
  () => selectedAssistance.value.maintenance_case || null
);
const selectedWorkOrder = computed(
  () => selectedAssistance.value.work_order || null
);
const selectedAssistanceOutcome = computed(
  () => selectedAssistance.value.outcome || null
);
const assistancePermissions = computed(
  () => selectedAssistance.value.permissions || {}
);
const selectedResponsible = computed(
  () => selectedRealEstate.value.responsible || {}
);
const selectedTeamHandover = computed(
  () => selectedRealEstate.value.team_handover || {}
);
const selectedOperationalTimeline = computed(
  () => selectedRealEstate.value.timeline || []
);
const selectedStatus = computed(() =>
  ticketStatusMeta(selectedTicket.value?.product_status, { t })
);
const selectedWaiting = computed(() =>
  waitingOnMeta(selectedTicket.value?.waiting_on, { t })
);
const selectedSlaStatus = computed(() =>
  slaStatusMeta(selectedSla.value?.sla_status, { t })
);
const hasRealEstateContext = computed(() => {
  const realEstate = selectedRealEstate.value || {};
  return [
    realEstate.imovel,
    realEstate.listing,
    realEstate.lease,
    realEstate.contract,
    realEstate.transaction,
    realEstate.handover,
    realEstate.responsible?.assignee,
    realEstate.responsible?.team,
  ].some(Boolean);
});
const hasSelectedContext = computed(() => {
  const context = selectedTicket.value?.context || {};
  return Object.values(context).some(Boolean) || hasRealEstateContext.value;
});
const publicHelpUrl = computed(() => {
  if (!portalMeta.value.publicHelpEnabled) return '';
  return publicSupportHref({
    basePath: portalMeta.value.publicHelpBasePath,
    locale: locale.value,
  });
});
const agentSupportDialogError = computed(() =>
  !isLoading.value && !publicHelpUrl.value && !hasTickets.value
    ? 'Nenhum canal público de suporte está habilitado para esta conta. Abra um ticket interno para acionar o time.'
    : ''
);
const canSubmitTicket = computed(
  () => ticketForm.value.subject.trim() && ticketForm.value.description.trim()
);
const canReply = computed(
  () => replyForm.value.body.trim() || replyForm.value.attachments.length
);
const canUseAssistanceConsole = computed(() =>
  Object.values(assistancePermissions.value).some(Boolean)
);
const hasAssistanceWorkOrder = computed(() => !!selectedWorkOrder.value?.id);
const canSubmitAssistance = computed(
  () => selectedTicketId.value && assistancePermissions.value.can_prepare
);
const canReopenAssistance = computed(
  () =>
    selectedTicketId.value &&
    assistancePermissions.value.can_reopen &&
    assistanceReopenForm.value.reason.trim()
);
const canApproveAssistanceBudget = computed(
  () =>
    selectedWorkOrder.value?.has_budget &&
    !selectedWorkOrder.value?.budget_approved &&
    assistancePermissions.value.can_approve_budget
);
const canRecordAssistanceCost = computed(
  () =>
    selectedWorkOrder.value?.id &&
    assistanceCostForm.value.amount.toString().trim() &&
    assistancePermissions.value.can_register_cost &&
    (!selectedWorkOrder.value.has_budget ||
      selectedWorkOrder.value.budget_approved)
);
const canRecordAssistanceEvidence = computed(
  () =>
    selectedWorkOrder.value?.id &&
    assistancePermissions.value.can_record_evidence &&
    (assistanceEvidenceForm.value.body.trim() ||
      assistanceEvidenceForm.value.evidence_label.trim() ||
      assistanceEvidenceForm.value.evidence_url.trim() ||
      assistanceEvidenceForm.value.evidence_checksum.trim() ||
      assistanceEvidenceForm.value.evidence_description.trim())
);
const canRecordAssistanceOutcome = computed(
  () =>
    selectedTicketId.value &&
    assistancePermissions.value.can_record_outcome &&
    assistanceOutcomeForm.value.resolution_outcome &&
    assistanceOutcomeForm.value.satisfaction_score
);
const canViewAssistanceReport = computed(
  () =>
    assistancePermissions.value.can_view_report || canViewAccountTickets.value
);
const canUpdateTeamHandover = computed(
  () =>
    selectedTicketId.value &&
    canViewAccountTickets.value &&
    teamHandoverForm.value.reason.trim()
);
const usesTriageQueue = computed(
  () =>
    canViewAccountTickets.value &&
    (filters.value.sla_status ||
      filters.value.due_soon ||
      filters.value.assignee_id)
);

const trackSupportEvent = (event, properties = {}) => {
  try {
    trackQuintoEvent(track, event, {
      domain: 'support_tickets',
      ...properties,
    });
  } catch {
    // Tracking must not block support flows.
  }
};

const stats = computed(() => [
  {
    label: t('SUPPORT_TICKETS.STATS.VISIBLE'),
    value: portalMeta.value.total,
    helper: canViewAccountTickets.value
      ? t('SUPPORT_TICKETS.STATS.VISIBLE_ALL')
      : t('SUPPORT_TICKETS.STATS.VISIBLE_OWN'),
  },
  {
    label: t('SUPPORT_TICKETS.STATS.AWAITING'),
    value: portalMeta.value.waitingOnCustomer,
    helper: t('SUPPORT_TICKETS.STATS.AWAITING_HELPER'),
  },
  {
    label: t('SUPPORT_TICKETS.STATS.MINE'),
    value: portalMeta.value.mine,
    helper: t('SUPPORT_TICKETS.STATS.MINE_HELPER'),
  },
]);

const ticketTypeOptions = computed(() => buildTicketTypeOptions(t));
const priorityOptions = computed(() => buildPriorityOptions(t));
const assistanceCategoryOptions = computed(() =>
  buildAssistanceCategoryOptions(t)
);
const assistanceSeverityOptions = computed(() =>
  buildAssistanceSeverityOptions(t)
);
const assistanceResponsibilityOptions = computed(() =>
  buildAssistanceResponsibilityOptions(t)
);
const assistanceContextOptions = computed(() =>
  buildAssistanceContextOptions(t)
);
const evidenceKindOptions = computed(() => buildEvidenceKindOptions(t));
const assistanceOutcomeOptions = computed(() =>
  buildAssistanceOutcomeOptions(t)
);
const statusOptions = computed(() => buildStatusOptions(t));
const waitingOptions = computed(() => buildWaitingOptions(t));
const createdByOptions = computed(() =>
  buildCreatedByOptions(t, canViewAccountTickets.value)
);
const slaFilterOptions = computed(() => buildSlaFilterOptions(t));

const categoryOptions = computed(() => filterPayload.value.categories || []);
const assigneeOptions = computed(() => [
  { value: '', label: t('SUPPORT_TICKETS.FILTERS.UNASSIGNED') },
  ...(filterPayload.value.assignees || []),
]);
const teamOptions = computed(() => [
  { value: '', label: t('SUPPORT_TICKETS.OPERATION.UNASSIGNED_TEAM') },
  ...(filterPayload.value.teams || []),
]);

const quickFilters = computed(() => [
  { key: 'all', label: t('SUPPORT_TICKETS.QUICK_FILTERS.ALL') },
  { key: 'mine', label: t('SUPPORT_TICKETS.QUICK_FILTERS.MINE') },
  { key: 'waiting', label: t('SUPPORT_TICKETS.QUICK_FILTERS.AWAITING') },
  ...statusOptions.value.slice(1).map(option => ({
    key: `status:${option.value}`,
    label: option.label,
  })),
]);

const activeQuickFilter = computed(() => {
  if (filters.value.waiting_on === 'customer') return 'waiting';
  if (filters.value.created_by === 'me') return 'mine';
  if (filters.value.status) return `status:${filters.value.status}`;
  return 'all';
});

const contextLink = (kind, id) => {
  if (!id || !canViewAccountTickets.value) return '';
  if (kind === 'conversation')
    return account.accountScopedUrl(`conversations/${id}`);
  if (kind === 'contact') return account.accountScopedUrl(`history/${id}`);
  if (kind === 'imovel') return account.accountScopedUrl(`imoveis/${id}`);
  return '';
};

const contextContactLink = computed(() =>
  contextLink('contact', selectedTicket.value?.context?.contact?.id)
);
const contextConversationLink = computed(() =>
  contextLink('conversation', selectedTicket.value?.context?.conversation?.id)
);
const contextImovelLink = computed(() =>
  contextLink('imovel', selectedTicket.value?.context?.imovel?.id)
);
const operationImovelLink = computed(() =>
  contextLink('imovel', selectedRealEstate.value.imovel?.id)
);

const requestParams = () => {
  if (!usesTriageQueue.value) return filters.value;
  return {
    q: filters.value.q,
    sla_status:
      filters.value.sla_status === 'due_soon' ? '' : filters.value.sla_status,
    due_soon: filters.value.sla_status === 'due_soon',
    assignee_id: filters.value.assignee_id,
  };
};

function syncTeamHandoverForm(ticket) {
  const teamId =
    ticket?.real_estate?.responsible?.team?.id || ticket?.assigned_team?.id;
  teamHandoverForm.value = {
    assigned_team_id: teamId ? String(teamId) : '',
    reason: '',
  };
}

const loadTicket = async id => {
  if (!id) {
    selectedTicket.value = null;
    selectedTicketId.value = null;
    return;
  }

  isLoadingDetail.value = true;
  try {
    const { data } = await SupportTicketPortalAPI.show(id);
    selectedTicket.value = data.data;
    selectedTicketId.value = data.data?.id || id;
    syncTeamHandoverForm(data.data);
    assistanceActionError.value = '';
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoadingDetail.value = false;
  }
};

const selectTicket = async (
  ticket,
  { force = false, keepMobileState = false } = {}
) => {
  if (!ticket) {
    selectedTicket.value = null;
    selectedTicketId.value = null;
    mobileDetailOpen.value = false;
    return;
  }

  if (!force && selectedTicketId.value === ticket.id) {
    if (isMobile.value) mobileDetailOpen.value = true;
    return;
  }

  selectedTicketId.value = ticket.id;
  await loadTicket(ticket.id);
  if (isMobile.value && !keepMobileState) mobileDetailOpen.value = true;
};

const loadAssistanceReport = async () => {
  if (!canViewAssistanceReport.value) {
    assistanceReport.value = null;
    return;
  }

  isLoadingAssistanceReport.value = true;
  try {
    const { data } = await SupportTicketPortalAPI.assistanceReport();
    assistanceReport.value = data.data;
  } catch (error) {
    assistanceReport.value = null;
    useAlert(errorMessage(error));
  } finally {
    isLoadingAssistanceReport.value = false;
  }
};

const loadTickets = async ({ keepSelection = false } = {}) => {
  isLoading.value = true;
  try {
    const response = usesTriageQueue.value
      ? await SupportTicketPortalAPI.triage(requestParams())
      : await SupportTicketPortalAPI.list(requestParams());
    const { data } = response;
    tickets.value = data.data || [];
    portalMeta.value = normalizePortalMeta(data.meta || {});
    filterPayload.value = {
      ...defaultFilterPayload(),
      ...(data.filters || {}),
    };
    await loadAssistanceReport();

    if (!keepSelection) {
      await selectTicket(tickets.value[0] || null, { force: true });
      return;
    }

    const stillVisible = tickets.value.find(
      ticket => ticket.id === selectedTicketId.value
    );
    await selectTicket(stillVisible || tickets.value[0] || null, {
      force: true,
      keepMobileState: true,
    });
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const resetTicketForm = () => {
  ticketForm.value = defaultTicketForm();
};

const resetReplyForm = () => {
  replyForm.value = defaultReplyForm();
};

const resetAssistanceForm = () => {
  assistanceForm.value = defaultAssistanceForm();
};

const resetAssistanceReopenForm = () => {
  assistanceReopenForm.value = defaultAssistanceReopenForm();
};

const resetAssistanceCostForm = () => {
  assistanceCostForm.value = defaultAssistanceCostForm();
};

const resetAssistanceEvidenceForm = () => {
  assistanceEvidenceForm.value = defaultAssistanceEvidenceForm();
};

const resetAssistanceOutcomeForm = () => {
  assistanceOutcomeForm.value = defaultAssistanceOutcomeForm();
};

const resetTeamHandoverForm = () => {
  teamHandoverForm.value = defaultTeamHandoverForm();
};

const createTicket = async () => {
  if (!canSubmitTicket.value) {
    useAlert(t('SUPPORT_TICKETS.ALERTS.CREATE_REQUIRED'));
    return;
  }

  isCreating.value = true;
  try {
    const { data } = await SupportTicketPortalAPI.create(ticketForm.value);
    resetTicketForm();
    selectedTicket.value = data.data;
    selectedTicketId.value = data.data?.id;
    useAlert(t('SUPPORT_TICKETS.ALERTS.CREATED'));
    await loadTickets({ keepSelection: true });
  } catch (error) {
    useAlert(
      ticketForm.value.attachments.length
        ? uploadErrorMessage(error)
        : errorMessage(error)
    );
  } finally {
    isCreating.value = false;
  }
};

const createReply = async () => {
  if (!selectedTicketId.value || !canReply.value) {
    useAlert(t('SUPPORT_TICKETS.ALERTS.REPLY_REQUIRED'));
    return;
  }

  isReplying.value = true;
  try {
    const payload = {
      body: replyForm.value.body,
      product_status: replyForm.value.product_status || undefined,
      internal: canViewAccountTickets.value ? replyForm.value.internal : false,
      attachments: replyForm.value.attachments,
    };
    const { data } = await SupportTicketPortalAPI.createReply(
      selectedTicketId.value,
      payload
    );
    resetReplyForm();
    selectedTicket.value = data.data;
    useAlert(t('SUPPORT_TICKETS.ALERTS.REPLIED'));
    await loadTickets({ keepSelection: true });
  } catch (error) {
    useAlert(
      replyForm.value.attachments.length
        ? uploadErrorMessage(error)
        : errorMessage(error)
    );
  } finally {
    isReplying.value = false;
  }
};

const updateAssignee = async assigneeId => {
  if (!selectedTicketId.value || !canViewAccountTickets.value) return;
  isUpdatingAssignee.value = true;
  try {
    const { data } = await SupportTicketPortalAPI.assign(
      selectedTicketId.value,
      assigneeId
    );
    selectedTicket.value = data.data;
    useAlert(t('SUPPORT_TICKETS.ALERTS.ASSIGNED'));
    await loadTickets({ keepSelection: true });
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isUpdatingAssignee.value = false;
  }
};

const updatePriority = async priority => {
  if (!selectedTicketId.value || !canViewAccountTickets.value) return;
  isUpdatingPriority.value = true;
  try {
    const { data } = await SupportTicketPortalAPI.updatePriority(
      selectedTicketId.value,
      priority
    );
    selectedTicket.value = data.data;
    useAlert(t('SUPPORT_TICKETS.ALERTS.PRIORITY_UPDATED'));
    await loadTickets({ keepSelection: true });
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isUpdatingPriority.value = false;
  }
};

const updateTeamHandover = async () => {
  if (!canUpdateTeamHandover.value) return;
  isUpdatingTeamHandover.value = true;
  try {
    const { data } = await SupportTicketPortalAPI.handover(
      selectedTicketId.value,
      {
        assigned_team_id: teamHandoverForm.value.assigned_team_id || undefined,
        reason: teamHandoverForm.value.reason,
      }
    );
    selectedTicket.value = data.data;
    resetTeamHandoverForm();
    syncTeamHandoverForm(data.data);
    useAlert(t('SUPPORT_TICKETS.ALERTS.HANDOVER_UPDATED'));
    await loadTickets({ keepSelection: true });
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isUpdatingTeamHandover.value = false;
  }
};

const withAssistanceAction = async ({
  isLoadingRef,
  request,
  successMessage,
  reset,
}) => {
  if (!selectedTicketId.value) return;
  assistanceActionError.value = '';
  isLoadingRef.value = true;
  try {
    await request(selectedTicketId.value);
    reset?.();
    useAlert(successMessage);
    await loadTickets({ keepSelection: true });
  } catch (error) {
    assistanceActionError.value = errorMessage(error);
    useAlert(assistanceActionError.value);
  } finally {
    isLoadingRef.value = false;
  }
};

const createAssistance = () =>
  withAssistanceAction({
    isLoadingRef: isPreparingAssistance,
    request: id =>
      SupportTicketPortalAPI.createAssistance(
        id,
        buildAssistancePayload(assistanceForm.value)
      ),
    successMessage: t('SUPPORT_TICKETS.ASSISTANCE.ALERTS.PREPARED'),
    reset: resetAssistanceForm,
  });

const reopenAssistance = () =>
  withAssistanceAction({
    isLoadingRef: isReopeningAssistance,
    request: id =>
      SupportTicketPortalAPI.reopenAssistance(
        id,
        buildReopenPayload(assistanceReopenForm.value)
      ),
    successMessage: t('SUPPORT_TICKETS.ASSISTANCE.ALERTS.REOPENED'),
    reset: resetAssistanceReopenForm,
  });

const approveAssistanceBudget = () =>
  withAssistanceAction({
    isLoadingRef: isApprovingAssistanceBudget,
    request: id =>
      SupportTicketPortalAPI.approveAssistanceBudget(id, {
        notes: t('SUPPORT_TICKETS.ASSISTANCE.APPROVAL.DEFAULT_NOTE'),
      }),
    successMessage: t('SUPPORT_TICKETS.ASSISTANCE.ALERTS.APPROVED'),
  });

const recordAssistanceCost = () =>
  withAssistanceAction({
    isLoadingRef: isRecordingAssistanceCost,
    request: id =>
      SupportTicketPortalAPI.recordAssistanceCost(
        id,
        buildCostPayload(assistanceCostForm.value)
      ),
    successMessage: t('SUPPORT_TICKETS.ASSISTANCE.ALERTS.COST_RECORDED'),
    reset: resetAssistanceCostForm,
  });

const recordAssistanceEvidence = () =>
  withAssistanceAction({
    isLoadingRef: isRecordingAssistanceEvidence,
    request: id =>
      SupportTicketPortalAPI.recordAssistanceEvidence(
        id,
        buildEvidencePayload(assistanceEvidenceForm.value)
      ),
    successMessage: t('SUPPORT_TICKETS.ASSISTANCE.ALERTS.EVIDENCE_RECORDED'),
    reset: resetAssistanceEvidenceForm,
  });

const recordAssistanceOutcome = () =>
  withAssistanceAction({
    isLoadingRef: isRecordingAssistanceOutcome,
    request: id =>
      SupportTicketPortalAPI.recordAssistanceOutcome(
        id,
        buildOutcomePayload(assistanceOutcomeForm.value)
      ),
    successMessage: t('SUPPORT_TICKETS.ASSISTANCE.ALERTS.OUTCOME_RECORDED'),
    reset: resetAssistanceOutcomeForm,
  });

const applyQuickFilter = key => {
  filters.value = defaultFilters();
  if (key === 'mine') filters.value.created_by = 'me';
  if (key === 'waiting') filters.value.waiting_on = 'customer';
  if (key.startsWith('status:')) filters.value.status = key.split(':')[1];
  loadTickets();
};

const clearFilters = () => {
  filters.value = defaultFilters();
  loadTickets();
};

const submitFilters = () => {
  showFilterDrawer.value = false;
  loadTickets();
};

const createAgentSupportChat = () => {
  showAgentSupportDialog.value = false;
  trackSupportEvent('agent_support_create_chat_clicked');
  if (agentSupportDialogError.value) {
    trackSupportEvent('create_chat_failure_snackbar_viewed', {
      reason: 'support_channel_unavailable',
    });
  }
  useAlert('Use o formulário de novo ticket para acionar o suporte TP/CX.');
};

const openAgentSupportDialog = () => {
  showAgentSupportDialog.value = true;
  trackSupportEvent('agent_support_dialog_opened');
};

const closeAgentSupportDialog = () => {
  showAgentSupportDialog.value = false;
  trackSupportEvent('agent_support_dialog_closed');
};

onMounted(() => {
  loadTickets();
});
</script>

<template>
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      :title="t('SUPPORT_TICKETS.HEADER.TITLE')"
      :helper="t('SUPPORT_TICKETS.HEADER.HELPER')"
      sticky
    >
      <template #actions>
        <a
          v-if="publicHelpUrl"
          :href="publicHelpUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-4 py-2 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary"
        >
          <span class="i-lucide-external-link size-4" aria-hidden="true" />
          <span>{{ t('SUPPORT_TICKETS.HEADER.PUBLIC_CENTER') }}</span>
        </a>
        <RouterLink
          :to="account.accountScopedRoute('manual_usuario_index')"
          class="inline-flex items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-4 py-2 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary"
        >
          <span class="i-lucide-book-open-text size-4" aria-hidden="true" />
          <span>{{ t('SUPPORT_TICKETS.HEADER.MANUAL') }}</span>
        </RouterLink>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-headset"
          @click="openAgentSupportDialog"
        >
          {{ agentSupportLabel }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div class="grid gap-4 md:grid-cols-3">
        <LaisCard v-for="item in stats" :key="item.label" density="tight">
          <p class="text-[12px] font-semibold text-lais-text-muted">
            {{ item.label }}
          </p>
          <p class="mt-2 text-[28px] font-semibold text-lais-text">
            {{ item.value }}
          </p>
          <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
            {{ item.helper }}
          </p>
        </LaisCard>
      </div>

      <div
        class="mt-5 grid gap-5 xl:grid-cols-[minmax(320px,0.78fr)_minmax(0,1.22fr)]"
      >
        <div
          class="grid gap-5 self-start"
          :class="{ 'hidden md:grid': mobileDetailOpen }"
        >
          <SupportTicketCreateForm
            v-model="ticketForm"
            :ticket-type-options="ticketTypeOptions"
            :priority-options="priorityOptions"
            :category-options="categoryOptions"
            :is-creating="isCreating"
            :can-submit-ticket="!!canSubmitTicket"
            @submit="createTicket"
          />

          <div class="md:hidden">
            <LaisPrimaryButton
              icon="i-lucide-list-filter"
              variant="ghost"
              @click="showFilterDrawer = true"
            >
              {{ t('SUPPORT_TICKETS.FILTERS.OPEN') }}
            </LaisPrimaryButton>
          </div>

          <SupportTicketFiltersPanel
            v-model="filters"
            :created-by-options="createdByOptions"
            :status-options="statusOptions"
            :waiting-options="waitingOptions"
            :sla-filter-options="slaFilterOptions"
            :assignee-options="assigneeOptions"
            :can-view-account-tickets="canViewAccountTickets"
            :is-loading="isLoading"
            @submit="submitFilters"
            @clear="clearFilters"
          />
        </div>

        <div
          class="grid min-w-0 gap-5"
          :class="{ 'md:grid hidden': mobileDetailOpen }"
        >
          <LaisCard
            :title="t('SUPPORT_TICKETS.LIST.TITLE')"
            prefix-icon="i-lucide-ticket"
            density="flush"
          >
            <div
              class="flex flex-wrap gap-2 border-b border-lais-border-soft p-4"
            >
              <button
                v-for="item in quickFilters"
                :key="item.key"
                type="button"
                class="rounded-full border px-3 py-1.5 text-[12px] font-semibold transition"
                :class="
                  activeQuickFilter === item.key
                    ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                    : 'border-lais-border-soft bg-white text-lais-text'
                "
                @click="applyQuickFilter(item.key)"
              >
                {{ item.label }}
              </button>
            </div>

            <SupportTicketSkeleton
              v-if="isLoading && !hasTickets"
              mode="list"
              :rows="4"
            />

            <div
              v-else-if="!hasTickets"
              class="flex min-h-[220px] flex-col items-center justify-center px-6 text-center"
            >
              <span
                class="i-lucide-inbox mb-3 size-8 text-lais-text-muted"
                aria-hidden="true"
              />
              <p class="text-[14px] font-semibold text-lais-text">
                {{ t('SUPPORT_TICKETS.LIST.EMPTY_TITLE') }}
              </p>
              <p
                class="mt-1 max-w-[360px] text-[12px] leading-5 text-lais-text-muted"
              >
                {{ t('SUPPORT_TICKETS.LIST.EMPTY_HELPER') }}
              </p>
            </div>

            <div v-else class="divide-y divide-lais-border-soft">
              <SupportTicketListItem
                v-for="ticket in tickets"
                :key="ticket.id"
                :ticket="ticket"
                :is-selected="selectedTicketId === ticket.id"
                :priority-options="priorityOptions"
                @select="selectTicket(ticket)"
              />
            </div>
          </LaisCard>
        </div>

        <div
          class="grid min-w-0 gap-5"
          :class="
            mobileDetailOpen
              ? 'fixed inset-0 z-40 overflow-auto bg-[#fcfaf6] p-4 md:static md:z-auto md:overflow-visible md:bg-transparent md:p-0'
              : ''
          "
        >
          <LaisCard
            :title="t('SUPPORT_TICKETS.DETAIL.TITLE')"
            prefix-icon="i-lucide-messages-square"
          >
            <template #suffix>
              <button
                v-if="isMobile && mobileDetailOpen"
                type="button"
                class="inline-flex items-center gap-2 rounded-lais border border-lais-border-soft px-3 py-1.5 text-[12px] font-semibold text-lais-text"
                @click="mobileDetailOpen = false"
              >
                <span class="i-lucide-arrow-left size-4" aria-hidden="true" />
                <span>{{ t('SUPPORT_TICKETS.LIST.BACK') }}</span>
              </button>
            </template>

            <SupportTicketSkeleton v-if="isLoadingDetail" mode="detail" />

            <div
              v-else-if="!selectedTicket"
              class="flex min-h-[240px] flex-col items-center justify-center text-center"
            >
              <span
                class="i-lucide-ticket-x mb-3 size-8 text-lais-text-muted"
                aria-hidden="true"
              />
              <p class="text-[14px] font-semibold text-lais-text">
                {{ t('SUPPORT_TICKETS.DETAIL.EMPTY_TITLE') }}
              </p>
              <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                {{ t('SUPPORT_TICKETS.DETAIL.EMPTY_HELPER') }}
              </p>
            </div>

            <div v-else class="grid gap-5">
              <SupportTicketDetailSummary
                :selected-ticket="selectedTicket"
                :selected-status="selectedStatus"
                :selected-waiting="selectedWaiting"
                :selected-sla="selectedSla"
                :selected-sla-status="selectedSlaStatus"
              />

              <SupportTicketTriagePanel
                v-if="canViewAccountTickets"
                :selected-ticket="selectedTicket"
                :assignee-options="assigneeOptions"
                :priority-options="priorityOptions"
                :is-updating-assignee="isUpdatingAssignee"
                :is-updating-priority="isUpdatingPriority"
                @update-assignee="updateAssignee"
                @update-priority="updatePriority"
              />

              <SupportTicketOperationPanel
                v-if="
                  hasRealEstateContext || selectedOperationalTimeline.length
                "
                v-model="teamHandoverForm"
                :selected-real-estate="selectedRealEstate"
                :selected-responsible="selectedResponsible"
                :selected-team-handover="selectedTeamHandover"
                :selected-operational-timeline="selectedOperationalTimeline"
                :selected-sla="selectedSla"
                :assistance-context-options="assistanceContextOptions"
                :priority-options="priorityOptions"
                :team-options="teamOptions"
                :can-view-account-tickets="canViewAccountTickets"
                :can-update-team-handover="!!canUpdateTeamHandover"
                :is-updating-team-handover="isUpdatingTeamHandover"
                :imovel-link="operationImovelLink"
                @submit="updateTeamHandover"
              />

              <SupportTicketContextPanel
                v-if="hasSelectedContext"
                :selected-ticket="selectedTicket"
                :contact-link="contextContactLink"
                :conversation-link="contextConversationLink"
                :imovel-link="contextImovelLink"
              />

              <section
                v-if="selectedAssistance"
                class="grid gap-4 rounded-lais border border-lais-border-soft bg-white px-4 py-3"
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 class="text-[13px] font-semibold text-lais-text">
                      {{ t('SUPPORT_TICKETS.ASSISTANCE.TITLE') }}
                    </h3>
                    <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                      {{ t('SUPPORT_TICKETS.ASSISTANCE.HELPER') }}
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <span
                      class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
                    >
                      {{
                        selectedWorkOrder?.code ||
                        t('SUPPORT_TICKETS.ASSISTANCE.NO_WORK_ORDER')
                      }}
                    </span>
                    <span
                      v-if="selectedAssistanceTicket.reopen_count"
                      class="rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-semibold text-amber-700"
                    >
                      {{
                        t('SUPPORT_TICKETS.ASSISTANCE.REOPEN_COUNT', {
                          count: selectedAssistanceTicket.reopen_count,
                        })
                      }}
                    </span>
                  </div>
                </div>

                <p
                  v-if="assistanceActionError"
                  class="rounded-lais border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-semibold text-rose-700"
                >
                  {{ assistanceActionError }}
                </p>

                <div
                  v-if="!canUseAssistanceConsole"
                  class="flex items-start gap-2 rounded-lais border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
                >
                  <span class="i-lucide-lock-keyhole size-4 shrink-0" />
                  <span>{{ t('SUPPORT_TICKETS.ASSISTANCE.PERMISSION') }}</span>
                </div>

                <SupportAssistanceSummaryCards
                  :selected-assistance-ticket="selectedAssistanceTicket"
                  :selected-maintenance-case="selectedMaintenanceCase"
                  :selected-work-order="selectedWorkOrder"
                  :assistance-context-options="assistanceContextOptions"
                  :assistance-severity-options="assistanceSeverityOptions"
                  :assistance-category-options="assistanceCategoryOptions"
                />

                <SupportAssistanceReport
                  v-if="canViewAssistanceReport"
                  :assistance-report="assistanceReport"
                  :is-loading-assistance-report="isLoadingAssistanceReport"
                />

                <div
                  v-if="selectedAssistanceOutcome"
                  class="grid gap-2 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3 text-[12px] text-lais-text-muted"
                >
                  <p class="text-[12px] font-semibold text-lais-text">
                    {{ t('SUPPORT_TICKETS.ASSISTANCE.OUTCOME.LATEST') }}
                  </p>
                  <p>
                    {{
                      assistanceLabel(
                        assistanceOutcomeOptions,
                        selectedAssistanceOutcome.resolution_outcome
                      )
                    }}
                    {{
                      t('SUPPORT_TICKETS.ASSISTANCE.OUTCOME.SCORE_VALUE', {
                        score: selectedAssistanceOutcome.satisfaction_score,
                      })
                    }}
                  </p>
                  <p v-if="selectedAssistanceOutcome.recurrence">
                    {{ t('SUPPORT_TICKETS.ASSISTANCE.OUTCOME.RECURRENCE') }}
                  </p>
                </div>

                <SupportAssistancePrepareForm
                  v-if="assistancePermissions.can_prepare"
                  v-model="assistanceForm"
                  :assistance-context-options="assistanceContextOptions"
                  :assistance-category-options="assistanceCategoryOptions"
                  :assistance-severity-options="assistanceSeverityOptions"
                  :assistance-responsibility-options="
                    assistanceResponsibilityOptions
                  "
                  :is-preparing-assistance="isPreparingAssistance"
                  :can-submit-assistance="!!canSubmitAssistance"
                  @submit="createAssistance"
                />

                <div
                  v-else-if="!hasAssistanceWorkOrder"
                  class="rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-2 text-[12px] text-lais-text-muted"
                >
                  {{ t('SUPPORT_TICKETS.ASSISTANCE.NO_PREPARE_PERMISSION') }}
                </div>

                <SupportAssistanceWorkOrderPanel
                  v-if="hasAssistanceWorkOrder"
                  v-model:reopen-form="assistanceReopenForm"
                  v-model:cost-form="assistanceCostForm"
                  v-model:evidence-form="assistanceEvidenceForm"
                  :selected-work-order="selectedWorkOrder"
                  :assistance-permissions="assistancePermissions"
                  :assistance-responsibility-options="
                    assistanceResponsibilityOptions
                  "
                  :evidence-kind-options="evidenceKindOptions"
                  :can-reopen-assistance="!!canReopenAssistance"
                  :can-approve-assistance-budget="!!canApproveAssistanceBudget"
                  :can-record-assistance-cost="!!canRecordAssistanceCost"
                  :can-record-assistance-evidence="
                    !!canRecordAssistanceEvidence
                  "
                  :is-reopening-assistance="isReopeningAssistance"
                  :is-approving-assistance-budget="isApprovingAssistanceBudget"
                  :is-recording-assistance-cost="isRecordingAssistanceCost"
                  :is-recording-assistance-evidence="
                    isRecordingAssistanceEvidence
                  "
                  @reopen="reopenAssistance"
                  @approve-budget="approveAssistanceBudget"
                  @record-cost="recordAssistanceCost"
                  @record-evidence="recordAssistanceEvidence"
                />

                <SupportAssistanceOutcomeForm
                  v-if="assistancePermissions.can_record_outcome"
                  v-model="assistanceOutcomeForm"
                  :assistance-outcome-options="assistanceOutcomeOptions"
                  :is-recording-assistance-outcome="
                    isRecordingAssistanceOutcome
                  "
                  :can-record-assistance-outcome="!!canRecordAssistanceOutcome"
                  @submit="recordAssistanceOutcome"
                />
              </section>

              <SupportTicketEventList :selected-events="selectedEvents" />

              <SupportTicketReplyForm
                v-model="replyForm"
                :status-options="statusOptions"
                :can-view-account-tickets="canViewAccountTickets"
                :is-replying="isReplying"
                :can-reply="!!canReply"
                @submit="createReply"
              />
            </div>
          </LaisCard>
        </div>
      </div>
    </div>

    <AgentSupportDialog
      :show="showAgentSupportDialog"
      :error-message="agentSupportDialogError"
      :public-help-url="publicHelpUrl"
      @close="closeAgentSupportDialog"
      @create-chat="createAgentSupportChat"
    />

    <SupportTicketFilterDrawer
      v-model="filters"
      :show="showFilterDrawer"
      :created-by-options="createdByOptions"
      :status-options="statusOptions"
      :waiting-options="waitingOptions"
      :sla-filter-options="slaFilterOptions"
      :assignee-options="assigneeOptions"
      :can-view-account-tickets="canViewAccountTickets"
      @close="showFilterDrawer = false"
      @clear="clearFilters"
      @submit="submitFilters"
    />
  </section>
</template>
